import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { type DragHandlerStore } from '../states/TableHandlerState';

export interface DragHandleOptions {
  /**
   * The width of the drag handle
   */
  dragHandleWidth: number;
}
function absoluteRect(node: Element) {
  const data = node.getBoundingClientRect();
  const modal = node.closest('[role="dialog"]');

  if (modal && window.getComputedStyle(modal).transform !== 'none') {
    const modalRect = modal.getBoundingClientRect();

    return {
      top: data.top - modalRect.top,
      left: data.left - modalRect.left,
      width: data.width,
      height: data.height,
    };
  }

  return {
    top: data.top,
    left: data.left,
    width: data.width,
    height: data.height,
  };
}

function nodeDOMAtCoords(coords: { x: number; y: number }) {
  return document
    .elementsFromPoint(coords.x, coords.y)
    .find(
      (elem: Element) =>
        elem.parentElement?.matches?.('.tableWrapper') ||
        elem.matches('tr, td, th'),
    );
}

function nodePosAtDOM(node: Element, view: EditorView) {
  const boundingRect = node.getBoundingClientRect();

  return view.posAtCoords({
    left: boundingRect.left,
    top: boundingRect.top,
  });
}

export function TableHandlesPlugin({
  dragHandlerState,
}: {
  dragHandlerState: DragHandlerStore;
}) {
  function hideNodeActions() {
    dragHandlerState.setState({
      visible: false,
    });
  }

  function showNodeActions() {
    dragHandlerState.setState({
      visible: true,
    });
  }

  return new Plugin({
    key: new PluginKey('tableHandles'),
    view: () => {
      hideNodeActions();

      return {};
    },
    props: {
      handleDOMEvents: {
        mousemove: (view, event) => {
          if (!view.editable) {
            return;
          }

          let node = nodeDOMAtCoords({
            x: event.clientX,
            y: event.clientY,
          });

          if (!(node instanceof Element)) {
            hideNodeActions();
            return;
          }

          const table = node.closest('table');
          const tableWrapper = table?.closest('.tableWrapper');

          if (!table || !tableWrapper) {
            hideNodeActions();
            return;
          }

          const tr = node.closest('tr');
          const cell = node?.closest('th, td');
          const nodeColIndex = Array.from(tr?.children || []).indexOf(cell!);
          const columnCells = Array.from(
            table.querySelectorAll(`tr > *:nth-child(${nodeColIndex + 1})`),
          );

          const firstCell = columnCells.at(0);
          const lastCell = columnCells.at(-1);
          const firstCellRect = firstCell?.getBoundingClientRect();
          const lastCellRect = lastCell?.getBoundingClientRect();

          if (!firstCellRect || !lastCellRect || !tr) {
            hideNodeActions();
            return;
          }
          const tableWrapperRect = tableWrapper.getBoundingClientRect();

          const colLeft = Math.max(firstCellRect.left, tableWrapperRect.left);
          const colRight = Math.min(
            firstCellRect.right,
            tableWrapperRect.right,
          );
          const colWidth = colRight - colLeft;

          const colRects = new DOMRect(
            colLeft,
            firstCellRect.top - 1,
            colWidth,
            Math.min(
              lastCellRect.height,
              tableWrapperRect.height - tableWrapperRect.top + lastCellRect.top,
            ),
          );

          const rowBounds = absoluteRect(tr);

          const rowRect = new DOMRect(
            Math.max(rowBounds.left, tableWrapperRect.left),
            Math.max(rowBounds.top - 1, tableWrapperRect.top),
            Math.min(rowBounds.width + 1, tableWrapperRect.width),
            Math.min(rowBounds.height + 1, tableWrapperRect.height),
          );

          dragHandlerState.setState({
            columnRects: colRects,
            rowRects: rowRect,
            columnNodePos: nodePosAtDOM(node, view)?.pos ?? null,
            rowNodePos: nodePosAtDOM(tr, view)?.pos ?? null,
            tableNodePos: nodePosAtDOM(table, view)?.pos ?? null,
            totalRows: table.querySelectorAll('tr').length,
            totalColumns: table.querySelectorAll('th').length,
            currentRowIndex: tr.rowIndex,
            currentColumnIndex: nodeColIndex,
          });

          showNodeActions();
        },
        keydown: () => {
          hideNodeActions();
        },
        mousewheel: () => {
          hideNodeActions();
        },
      },
    },
  });
}
